import { useEffect, useRef, useState } from "react";
import { documents as api, type InvestorDocument } from "../../../api";
import { formatBytes, formatDateTime } from "../format";

export function InvestorDocuments({ investorId }: { investorId: number }) {
  const [items, setItems] = useState<InvestorDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<InvestorDocument | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  // Reload by bumping reloadCount; the effect below handles the request and
  // ignores stale responses if investorId/reloadCount has changed in flight.
  useEffect(() => {
    let cancelled = false;
    api
      .list(investorId)
      .then((d) => {
        if (!cancelled) setItems(d.documents);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [investorId, reloadCount]);

  async function onFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      await api.upload(investorId, file);
      setReloadCount((n) => n + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.remove(investorId, pendingDelete.id);
      setPendingDelete(null);
      setReloadCount((n) => n + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section>
      <div className="tab-toolbar">
        <div className="muted">Files attached to this investor.</div>
        <div>
          <input
            ref={fileInput}
            type="file"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
          >
            {uploading ? "Uploading…" : "Upload document"}
          </button>
        </div>
      </div>

      {error && <div className="banner-error">{error}</div>}

      {items === null ? (
        <div className="muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>No documents yet.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Filename</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th className="cell-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td className="strong">{d.filename}</td>
                <td>{formatBytes(d.size)}</td>
                <td>{formatDateTime(d.uploaded_at)}</td>
                <td className="cell-actions">
                  <a
                    href={api.downloadUrl(investorId, d.id)}
                    className="link-action"
                  >
                    Download
                  </a>
                  <button
                    type="button"
                    className="link-action danger"
                    onClick={() => setPendingDelete(d)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pendingDelete && (
        <div className="modal-backdrop" onClick={() => setPendingDelete(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete document?</h2>
            <p>
              <strong>{pendingDelete.filename}</strong> will be removed. This
              can't be undone.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete document"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
